import { Link, useParams } from 'react-router-dom';
import { reports, type DomainScore } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTranslation, type TKey } from '../i18n';
import { EmptyState, Sidenote } from '../components/ui';

const NAEP_ORDER = ['locate_recall', 'integrate_interpret', 'critique_evaluate'];

function pct(v: number) {
  return Math.round((v ?? 0) * 100);
}

export default function ReportPage() {
  const { t } = useTranslation();
  const { bookId = '' } = useParams();
  const state = useApi(() => reports.byBook(bookId), [bookId]);

  function DomainBar({ label, d }: { label: string; d: DomainScore }) {
    const attempted = d?.attempted ?? 0;
    return (
      <div className={`report-dom${attempted === 0 ? ' is-empty' : ''}`}>
        <div className="report-dom__top">
          <span className="report-dom__label">{label}</span>
          <span className="report-dom__meta">
            {attempted === 0
              ? t('report.notAttempted')
              : `${pct(d.accuracy)}% · ${attempted}${t('report.unit')}`}
          </span>
        </div>
        <div className="report-dom__bar">
          <div className="report-dom__fill" style={{ width: `${attempted === 0 ? 0 : pct(d.accuracy)}%` }} />
        </div>
      </div>
    );
  }

  return (
    <main className="container report-page">
      <header className="report-head">
        <Link to={`/books/${bookId}`} className="report-back">← {t('common.back')}</Link>
        <div className="kicker">{t('report.kicker')}</div>
        <h1 className="report-title">{t('report.title')}</h1>
      </header>

      {state.status === 'loading' && (
        <div className="skeleton-stack" aria-busy="true">
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-block" />
        </div>
      )}
      {state.status === 'error' && (
        <EmptyState tone="error" title={t('common.offline')} description={t('report.emptySub')} />
      )}

      {state.status === 'success' && state.data.total_attempts === 0 && (
        <EmptyState
          title={t('report.empty')}
          description={t('report.emptySub')}
          primary={<Link to={`/books/${bookId}/quiz`} className="btn btn-accent">{t('report.startQuiz')}</Link>}
        />
      )}

      {state.status === 'success' && state.data.total_attempts > 0 && (() => {
        const r = state.data;
        return (
          <>
            <section className="report-hero">
              <div className="report-hero__main">
                <div className="report-stat report-stat--lead">
                  <span className="report-stat__value">{pct(r.overall_accuracy)}<span className="report-stat__pct">%</span></span>
                  <span className="report-stat__label">{t('report.accuracy')}</span>
                </div>
                <div className="report-hero__side">
                  <div className="report-stat">
                    <span className="report-chip">{r.overall_level}</span>
                    <span className="report-stat__label">{t('report.level')}</span>
                  </div>
                  <div className="report-stat">
                    <span className="report-stat__num">{r.theta == null ? '—' : r.theta.toFixed(2)}</span>
                    <span className="report-stat__label">{t('report.theta')}</span>
                  </div>
                  <div className="report-stat">
                    <span className="report-stat__num">{r.total_attempts}</span>
                    <span className="report-stat__label">{t('report.attempted')}</span>
                  </div>
                </div>
              </div>
              <aside className="report-hero__aside">
                <Sidenote label={t('report.honestyLabel')} tone="mute">
                  <p>{t('report.honestyBody')}</p>
                  <p className="mono">θ: {t('report.thetaNote')}</p>
                </Sidenote>
              </aside>
            </section>

            <section className="report-section">
              <div className="sec-head"><h2>{t('report.naep')}</h2></div>
              {NAEP_ORDER.filter((k) => r.naep[k]).map((k) => (
                <DomainBar key={k} label={t(`report.naep.${k}` as TKey)} d={r.naep[k]} />
              ))}
            </section>

            <section className="report-section">
              <div className="sec-head"><h2>{t('report.lidia')}</h2></div>
              {Object.entries(r.lidia).map(([k, d]) => (
                <DomainBar key={k} label={k} d={d} />
              ))}
            </section>

            {r.trend.length > 0 && (
              <section className="report-section">
                <div className="sec-head"><h2>{t('report.trend')}</h2></div>
                <div className="report-trend">
                  {r.trend.map((p) => (
                    <div key={p.window} className="report-trend__col"
                      title={`${t('report.window')} ${p.window}: ${pct(p.accuracy)}%`}>
                      <div className="report-trend__fill" style={{ height: `${pct(p.accuracy)}%` }} />
                      <span className="report-trend__label">{p.window}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="report-actions">
              <Link to="/credentials" className="btn btn-secondary">{t('report.viewCredential')}</Link>
              <Link to="/achievements" className="btn btn-secondary">{t('report.viewAchievements')}</Link>
              <Link to={`/books/${bookId}/quiz`} className="btn btn-ghost">{t('report.retry')}</Link>
            </div>
          </>
        );
      })()}
    </main>
  );
}
